'use client';

import { proxyFetch } from '@ps/functions/utils/proxyFetch';

export interface GuestContactData {
  name?: string;
  email?: string;
  phone?: string;
}

/**
 * Hook that creates a guest Firebase account and updates the guest user's
 * profile with their contact information.
 *
 * Used after anonymous users complete a contact form (guest contact or
 * lead form contact step) before proceeding to an authenticated action
 * like booking a meeting slot.
 *
 * Pass the viewer's current `userId` (from `useCurrentUserId`) — a signed-in
 * viewer is a no-op. Firebase Auth is never imported here: guest creation
 * (`guest-auth`, which pulls firebase + reCAPTCHA) is dynamic-imported and only
 * runs for an anonymous viewer, so the SDK stays OFF the visitor bio's
 * first-load bundle.
 *
 * Flow:
 * 1. `createGuestAccount()` — creates Firebase guest user, signs in
 * 2. `updateUserV2()` — updates firstName, lastName, email on the guest profile
 *
 * Reusable across the app — not limited to the access guard.
 */
export function useCreateGuestAndUpdateInfo(userId: string | null | undefined) {
  async function createGuestAndUpdate(data: GuestContactData) {
    // Skip if already signed in (the caller gates this on an anonymous viewer;
    // kept defensive).
    if (userId != null) return;

    // Create guest Firebase account (reCAPTCHA + sign in). Dynamic-imported so
    // its firebase SDK loads ONLY on this path — `createGuestAccount` throws on
    // failure and returns the new uid, so a guest is signed in once it resolves.
    const { createGuestAccount } = await import('@ps/functions/guest-auth');
    await createGuestAccount();

    if (!data.name) return;

    try {
      const nameParts = data.name.trim().split(/\s+/);
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');

      await proxyFetch('/api/bio/user/update-profile', {
        method: 'POST',
        body: {
          firstName,
          lastName,
          ...(data.email ? { email: data.email.trim() } : {}),
        },
      });
    } catch (err) {
      console.warn(
        '[useCreateGuestAndUpdateInfo] Failed to update profile:',
        err,
      );
    }
  }

  return createGuestAndUpdate;
}
